import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UiButton, UiTextField } from 'ui-components';
import lines from 'assets/img/lines.svg';
import { usePlatform } from 'hooks';
import {
  setQueries,
  setCurrentQuery,
  applyFiltersToApiRequest,
  applyFiltersToAllPlatforms,
  setPlatformCurrentPageNumber,
} from 'store';
import SearchHistoryMenu from 'pages/search/components/SearchHistoryMenu';
import {
  magicNumbers,
  getExclamationMarkCount,
  getPlusAndAsteriskMarksCount,
  isResourcesListPath,
} from 'utils';
import SaveSubjectsModal from 'components/save-subjects-modal/SaveSubjectsModal';
import { StyledSmallSearch } from './SmallSearch.styles';

const SmallSearch = ({ variant = 'default' }) => {
  const dispatch = useDispatch();
  const currentQuery = useSelector((state) => state.pageReducer.currentQuery);
  const queries = useSelector((state) => state.pageReducer.queries);
  const location = useLocation();
  /**
   * Only the resources directory (/resources, /resources/:platform) searches
   * by free text unrelated to subjects. A single resource's profile page
   * (/resources/:platform/:id) filters that resource's own content by subject
   * exactly like the contents page, so it must behave the same way here too —
   * showing the selected subject's parsed_query and writing typed searches to
   * q/queryId, not the unrelated resourceQuery field.
   */
  const searchType = isResourcesListPath(location.pathname)
    ? 'resource'
    : 'content';
  const isHeaderVariant = variant === 'header';

  const selectedQuery = queries.data?.find(
    (item) => item.id === currentQuery.queryId
  );
  let parsedQuery = selectedQuery?.parsed_query ?? '';
  if (searchType === 'resource') {
    parsedQuery = currentQuery.resourceQuery ?? '';
  }
  const [searchText, setSearchText] = useState();
  const [isInputFocused, setIsInputFocused] = useState(false);
  const showHistoryMenu = isInputFocused && searchText?.length === 0;
  const [error, setError] = useState('');
  const { t } = useTranslation();
  const { getSettingsDefaultPlatform } = usePlatform();
  const { platform: activePlatform = getSettingsDefaultPlatform() } =
    useParams();

  const handleSubmit = () => {
    dispatch(
      setPlatformCurrentPageNumber({ platform: activePlatform, pageNumber: 1 })
    );

    const notSymbol = getExclamationMarkCount(searchText);
    const plusAndAsteriskSymbols = getPlusAndAsteriskMarksCount(searchText);

    /**
     * اگر تعداد علامت های نات از سایر علامت ها کسر شود و نتیجه کمتر از 0 باشد شرط اجرا شود
     */
    if (plusAndAsteriskSymbols - notSymbol < 0) {
      setError(t('common:search.notAllowedContent'));
      return;
    }

    setError('');
    if (searchType === 'resource') {
      dispatch(
        setCurrentQuery({
          resourceQuery: searchText,
        })
      );
      dispatch(applyFiltersToApiRequest());
      return;
    }

    let queryList = [...queries.data];
    if (
      !queryList.some((item) => item?.id === magicNumbers.YOUR_SEARCH_QUERY_ID)
    ) {
      queryList.unshift({
        id: magicNumbers.YOUR_SEARCH_QUERY_ID,
        parsed_query: searchText,
        title: t('common:search.yourSearch'),
      });
    } else {
      queryList = queryList.filter(
        (item) => item?.id !== magicNumbers.YOUR_SEARCH_QUERY_ID
      );
      queryList.unshift({
        id: magicNumbers.YOUR_SEARCH_QUERY_ID,
        parsed_query: searchText,
        title: t('common:search.yourSearch'),
      });
    }
    dispatch(
      setCurrentQuery({
        lastQueryId: currentQuery.queryId, // آخرین آیدی رو نگه میداریم که اگر جایی نیاز بود برگردیم به همون اخرین کوئری
        q: searchText,
        lastSearchQuery: searchText, // باید اگر قبلا از این مقدار جایی استفاده میشد(مثل تگها، موضوعات و منبع) این هم با مقدار جدید کوئری یکسان باشه
        queryId: magicNumbers.YOUR_SEARCH_QUERY_ID,
        // A real search replaces the stand-in subject kept for empty free search
        freeSearchFallbackQueryId: 0,
      })
    );
    dispatch(setQueries(queryList));
    if (searchType === 'content') {
      dispatch(applyFiltersToApiRequest());
    }
    dispatch(applyFiltersToAllPlatforms());
  };

  const handleChangeSearchValue = (e) => {
    setSearchText(e.target.value.replace(/\n/, ''));
    setError('');
  };

  useEffect(() => {
    setSearchText(parsedQuery);
  }, [currentQuery, parsedQuery]);

  // اگر مقدار کیو در هیچ‌یک از موضوعات ذخیره‌شده وجود نداشته باشد true برمی‌گرداند
  const isUniqueQuery = () => {
    return !queries.data?.some(
      (item) =>
        item.id !== magicNumbers.YOUR_SEARCH_QUERY_ID &&
        item.parsed_query === currentQuery.q
    );
  };

  const handleInputFocus = () => {
    setIsInputFocused(true);
  };

  const handleInputBlur = () => {
    setIsInputFocused(false);
  };

  /**
   * The resources list writes to resourceQuery, not q/queryId, so it can never
   * build a real subject to save — that page must never offer it, even if
   * queryId is still YOUR_SEARCH_QUERY_ID from a page visited earlier. The
   * resource profile page now behaves like content, so it keeps the offer.
   */
  const showSaveSubject =
    searchType !== 'resource' &&
    currentQuery?.queryId === magicNumbers.YOUR_SEARCH_QUERY_ID &&
    Boolean(currentQuery.q) &&
    isUniqueQuery();

  return (
    <StyledSmallSearch className={isHeaderVariant ? 'is-header-variant' : ''}>
      <div className='small-search'>
        {!isHeaderVariant && (
          <img className='search-lines' src={lines} alt='' />
        )}
        <div className='search-form'>
          <div className='form-input-anchor'>
            <UiTextField
              value={searchText || ''}
              onChange={handleChangeSearchValue}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && searchText?.length) {
                  handleSubmit();
                }
              }}
              className='form-input'
              placeholder={t('common:search.inputPlaceholder')}
              multiline
              error={!!error}
              inputProps={{ 'data-testid': 'small-search-input' }}
            />
            {showHistoryMenu && (
              <SearchHistoryMenu
                setSearchText={setSearchText}
                setIsInputFocused={setIsInputFocused}
                searchType={searchType}
              />
            )}
          </div>
          {showSaveSubject && <SaveSubjectsModal />}
          <UiButton
            onClick={handleSubmit}
            variant='contained'
            color='primary'
            disabled={!(searchText?.length > 1) || searchText === parsedQuery}
            className='form-button'
            data-testid='small-search-submit-btn'
          >
            {t('common:search')}
          </UiButton>
          {error && <p className='search-form-input-helper'>{error}</p>}
        </div>
      </div>
    </StyledSmallSearch>
  );
};

SmallSearch.propTypes = {
  variant: PropTypes.oneOf(['default', 'header']),
};

export default SmallSearch;
